import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/data';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 group whitespace-nowrap"
          aria-label="亚洲一级日本精选内容导航平台 - 首页"
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`ri-sun-line text-xl ${scrolled || !isHome ? 'text-primary-500' : 'text-background-50'}`}></i>
          </div>
          <span
            className={`font-heading font-bold text-base transition-colors duration-300 ${
              scrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            亚洲一级日本
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              location.pathname === '/'
                ? scrolled
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-background-50/20 text-background-50'
                : scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
            }`}
          >
            首页
          </Link>

          <div className="relative group">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              分类浏览
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-xs"></i>
              </div>
            </button>
            <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-lg border border-background-200/70 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors duration-150"
                >
                  <div className={`w-7 h-7 flex items-center justify-center rounded-md ${
                    cat.color === 'primary' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                  }`}>
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.name}</p>
                    <p className="text-xs text-foreground-500">{cat.articleCount} 篇内容</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/about"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              location.pathname === '/about'
                ? scrolled
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-background-50/20 text-background-50'
                : scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
            }`}
          >
            关于我们
          </Link>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            scrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
          }`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? '关闭菜单' : '打开菜单'}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
          </div>
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100">
              首页
            </Link>
            <p className="px-4 py-3 text-sm font-medium text-foreground-500">分类浏览</p>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 px-6 py-2.5 rounded-lg hover:bg-background-100"
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-md ${
                  cat.color === 'primary' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                }`}>
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{cat.name}</p>
                  <p className="text-xs text-foreground-500">{cat.articleCount} 篇内容</p>
                </div>
              </Link>
            ))}
            <Link to="/about" className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}